import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { UPasswordInput } from 'components/core/u-password-input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import {
  changePasswordFormDefaultValue,
  changePasswordFormType,
  changePasswordFormValidationSchema,
} from 'features/profile/utils/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useChangePassword } from 'features/profile/hooks/use-account';
import { UpdatePasswordSuccess } from '../update-password-success/update-password-success';
import { SharedPasswordStrengthChecker } from 'components/core/shared-password-strength-checker';
import { useTranslation } from 'react-i18next';

/**
 * Component to allow users to update their password.
 * It includes form validation, password strength checking, and feedback on successful or failed password change.
 *
 * @component
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onClose - The function to call when the modal/dialog should be closed.
 * @param {boolean} [props.open] - Optional flag to control the open state of the dialog.
 * @param {Function} [props.onOpenChange] - Optional function to be called when the open state of the dialog changes.
 *
 * @returns {JSX.Element} - The rendered component.
 *
 * @example
 * // Example usage
 * <UpdatePassword
 *   open={isDialogOpen}
 *   onClose={() => setDialogOpen(false)}
 *   onOpenChange={setDialogOpen}
 * />
 */

type UpdatePasswordProps = {
  onClose: () => void;
  open?: boolean;
  onOpenChange?(open: boolean): void;
};

// Figma 10596:1932: 44px fields with 12px corners.
const passwordInputClassName =
  'h-11 rounded-xl border-grey-100 bg-true-white text-sm dark:border-border dark:bg-card';

export const UpdatePassword: React.FC<UpdatePasswordProps> = ({ onClose, open, onOpenChange }) => {
  const { t } = useTranslation();
  const [passwordRequirementsMet, setPasswordRequirementsMet] = useState(false);
  const [updatePasswordSuccessModalOpen, setUpdatePasswordSuccessModalOpen] = useState(false);

  const form = useForm<changePasswordFormType>({
    defaultValues: changePasswordFormDefaultValue,
    resolver: zodResolver(changePasswordFormValidationSchema),
  });

  const { mutate: changePassword, isPending } = useChangePassword();

  const onSubmitHandler = async (values: changePasswordFormType) => {
    if (values.newPassword !== values.confirmNewPassword) {
      form.setError('confirmNewPassword', {
        type: 'manual',
        message: t('PASSWORDS_NOT_MATCH'),
      });
      return;
    }

    changePassword(
      {
        newPassword: values.newPassword,
        oldPassword: values.oldPassword,
      },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange?.(false);
          setUpdatePasswordSuccessModalOpen(true);
        },
        onError: (error) => {
          console.error('Error:', error);
          form.setError('oldPassword', {
            type: 'manual',
            message: error?.error?.message ?? t('FAILED_TO_CHANGE_PASSWORD'),
          });
        },
      }
    );
  };

  const password = form.watch('newPassword');
  const confirmPassword = form.watch('confirmNewPassword');

  const onModalClose = () => {
    setUpdatePasswordSuccessModalOpen(false);
    onClose();
    form.reset();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent nested>
          <DialogHeader>
            <DialogTitle className="text-lg font-medium tracking-normal text-grey-950 dark:text-foreground">
              {t('UPDATE_PASSWORD')}
            </DialogTitle>
            <DialogDescription className="text-sm font-normal text-grey-500 dark:text-muted-foreground">
              {t('SECURE_ACCOUNT_WITH_NEW_PASSWORD')}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmitHandler)} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="oldPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">
                        {t('CURRENT_PASSWORD')}
                      </FormLabel>
                      <FormControl>
                        <UPasswordInput
                          className={passwordInputClassName}
                          placeholder={t('ENTER_CURRENT_PASSWORD')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-xs font-normal leading-heading text-error-700" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">
                        {t('NEW_PASSWORD')}
                      </FormLabel>
                      <FormControl>
                        <UPasswordInput
                          className={passwordInputClassName}
                          placeholder={t('ENTER_NEW_PASSWORD')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-xs font-normal leading-heading text-error-700" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmNewPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">
                        {t('CONFIRM_NEW_PASSWORD')}
                      </FormLabel>
                      <FormControl>
                        <UPasswordInput
                          className={passwordInputClassName}
                          placeholder={t('CONFIRM_YOUR_NEW_PASSWORD')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-xs font-normal leading-heading text-error-700" />
                    </FormItem>
                  )}
                />
                <SharedPasswordStrengthChecker
                  password={password}
                  confirmPassword={confirmPassword}
                  onRequirementsMet={setPasswordRequirementsMet}
                />
              </div>
              <DialogFooter className="mt-2 gap-2 sm:space-x-0">
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    disabled={isPending}
                    onClick={onModalClose}
                    className="h-10 rounded-full px-4 text-sm font-medium text-grey-900 shadow-none hover:bg-grey-50 dark:text-foreground dark:hover:bg-muted"
                  >
                    {t('CANCEL')}
                  </Button>
                </DialogTrigger>
                <Button
                  type="submit"
                  disabled={isPending || !passwordRequirementsMet}
                  className="h-10 rounded-full bg-grey-950 px-5 text-sm font-medium text-true-white hover:bg-grey-900 dark:bg-foreground dark:text-background dark:hover:bg-foreground/90"
                >
                  {t('UPDATE')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={updatePasswordSuccessModalOpen}
        onOpenChange={() => setUpdatePasswordSuccessModalOpen(false)}
      >
        <UpdatePasswordSuccess
          onClose={() => {
            setUpdatePasswordSuccessModalOpen(false);
            onClose();
          }}
        />
      </Dialog>
    </>
  );
};
